import { AlertTriangle, Clock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '../../lib/authStore.js';

/**
 * Compact billing reminder shown at the top of the tenant app when the
 * tenant is behind on payment, in a soon-to-expire trial, or has a
 * current_period_end within 7 days.
 *
 * The platform admin controls all billing state — there is no
 * "Pay now" button here; we instruct the tenant to contact support.
 * Suspended tenants don't see this because they can't log in at all.
 */
export function BillingBanner(): JSX.Element | null {
  const { t } = useTranslation('platform');
  const tenant = useAuthStore((s) => s.tenant);
  if (!tenant) return null;

  const status = tenant.billingStatus;
  const expiresAtIso = tenant.currentPeriodEnd ?? tenant.trialEndsAt ?? null;
  const expiresAt = expiresAtIso ? new Date(expiresAtIso) : null;
  const daysLeft = expiresAt
    ? Math.ceil((expiresAt.getTime() - Date.now()) / 86_400_000)
    : null;

  const overdue = daysLeft !== null && daysLeft < 0;
  const dueSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;

  // Show conditions:
  //  * past_due or unpaid → always red
  //  * cancelled → red but informational
  //  * trialing AND <=7 days left → yellow
  //  * any status AND <=7 days left (but >=0) → yellow nudge
  //  * overdue current_period_end with active status → yellow (operator
  //    just hasn't flipped past_due yet)
  if (
    status !== 'past_due' &&
    status !== 'unpaid' &&
    status !== 'cancelled' &&
    !overdue &&
    !(dueSoon && (status === 'trialing' || status === 'active'))
  ) {
    return null;
  }

  const isRed = status === 'past_due' || status === 'unpaid' || status === 'cancelled';
  const Icon = isRed ? AlertTriangle : Clock;
  const toneClass = isRed
    ? 'border-danger bg-danger/10 text-danger'
    : 'border-warning bg-warning/10 text-warning';

  const message = isRed
    ? t(`billingBanner.${status}`, {
        defaultValue:
          status === 'past_due'
            ? 'Your account is past due. Contact support to keep things running.'
            : status === 'unpaid'
              ? 'Your account is unpaid. Contact your account manager urgently — access will be revoked soon.'
              : 'Your subscription has been cancelled — read-only access only.',
      })
    : status === 'trialing'
      ? t('billingBanner.trialEnding', {
          days: daysLeft ?? 0,
          defaultValue: 'Your trial ends in {{days}} days. Contact your account manager to subscribe.',
        })
      : t('billingBanner.dueSoon', {
          days: daysLeft ?? 0,
          defaultValue: 'Your subscription expires in {{days}} days. Contact your account manager to extend.',
        });

  return (
    <div
      className={`flex items-center justify-between gap-3 border-b-2 px-6 py-2 text-sm ${toneClass}`}
      role="status"
      aria-live="polite"
    >
      <span className="flex items-center gap-2">
        <Icon className="h-4 w-4" aria-hidden />
        {message}
      </span>
    </div>
  );
}
